import { z } from "zod";

import { baseEntityFields } from "./base.js";
import type { Member } from "./member.js";

/**
 * LA PERSONNE : un utilisateur de l'org, designe par un identifiant qui ne
 * change jamais.
 *
 * POURQUOI UN IDENTIFIANT DE PLUS. La fiche membre a pour id l'E-MAIL
 * (`member.ts`), qui change le jour ou quelqu'un change d'adresse ; l'uid
 * Firebase n'existe pas avant la premiere connexion, alors qu'on veut deja
 * designer la personne invitee. Le `personId` est tire au sort a la creation
 * de la fiche (un id Firestore, sans sens), pose une fois, jamais affiche.
 *
 * L'ANNUAIRE DES PERSONNES est la sous-collection `orgs/{orgId}/personnes`,
 * une entree par personne, LISIBLE par tout membre actif : c'est ce qui
 * permet d'afficher un NOM la ou une fiche porte un `personId`, sans ouvrir
 * les fiches membres (reservees aux administrateurs). Il ne porte ni role ni
 * derogation. Une personne retiree y RESTE, archivee : les fiches du passe
 * doivent continuer a porter son nom.
 *
 * Ce n'est PAS une famille du SDK (comme `members`) : pas de `type`, pas de
 * `customFields`, pas de porte. `sdk.personnes` la lit, et c'est celui qui
 * ecrit la fiche membre qui ecrit l'entree, dans le meme lot.
 */

/** Le nom de l'annuaire, celui d'une `reference.family` qui designe une personne. */
export const FAMILLE_PERSONNES = "personnes";

/**
 * Le sous-type d'annuaire qui range les personnes par UID plutot que par
 * `personId` : c'est ainsi que `createdBy` / `updatedBy` (qui portent un uid)
 * se nomment. Un invite sans compte n'y figure pas.
 */
export const TYPE_ANNUAIRE_PAR_UID = "uid";

/** La marque posee sur un champ natif qui porte un `personId`, lue par `genreDe`. */
export const MARQUE_PERSONNE = "personne";

/**
 * LE CHAMP NATIF QUI DESIGNE UNE PERSONNE (un `personId`). Un `string` marque
 * par sa description : c'est elle que `champsNatifs` lit pour rendre le genre
 * `personne`, donc la marque doit rester sur le `ZodString` lui-meme. Ne
 * JAMAIS l'envelopper d'un `.refine` ou d'un `.transform` : `deballer` garde
 * le type interieur, et la description d'un `ZodEffects` serait perdue.
 * `.optional()` et `.nullable()` sont sans danger.
 */
export const personneId = () => z.string().min(1).describe(MARQUE_PERSONNE);

/**
 * LA BORNE D'UNE PHOTO DE PROFIL : une URL, jamais une image. Les rules
 * (`orgs/{orgId}/personnes`) en recopient la valeur : un test de `@taki/sdk`
 * les recoupe.
 */
export const PHOTO_URL_MAX = 2048;

/**
 * L'URL d'une photo de profil, ou `null` quand elle ne se range pas : https
 * seulement (un `http:`, un `data:` ou un `javascript:` ne sont jamais
 * affiches), sans espace, au plus `PHOTO_URL_MAX` caracteres. Pure : le SDK
 * l'applique avant d'ecrire, les rules refont le meme test de leur cote.
 */
export function photoURLValide(valeur: string | null | undefined): string | null {
  if (typeof valeur !== "string") return null;
  const url = valeur.trim();
  if (url === "" || url.length > PHOTO_URL_MAX) return null;
  if (!url.startsWith("https://") || url.length === "https://".length) return null;
  if (/\s/u.test(url)) return null;
  return url;
}

/**
 * Une entree de l'annuaire. `personId` est l'id du document ; `userId` est
 * `null` tant que la personne ne s'est jamais connectee. Le STATUT (invite ou
 * actif) n'est pas stocke : il se deduit de `userId` (`statutDePersonne`), un
 * fait ecrit deux fois finirait par diverger.
 */
export const PersonneSchema = z.object({
  ...baseEntityFields,
  orgId: z.string(),
  personId: z.string().min(1),
  displayName: z.string(),
  email: z.string().email(),
  userId: z.string().nullable(),
  /**
   * La photo de profil de la personne (celle de son compte Google), posee par
   * ELLE a sa connexion et quand elle change. Absente tant qu'elle n'en a pas ;
   * l'ecran retombe alors sur ses initiales.
   */
  photoURL: z.string().max(PHOTO_URL_MAX).nullable().optional(),
});

/** Une personne de l'annuaire. */
export type Personne = z.infer<typeof PersonneSchema>;

/** Invitee (aucun compte encore rattache) ou active. */
export type StatutDePersonne = "invite" | "actif";

/** Le statut d'une personne, DEDUIT de son `userId`. */
export function statutDePersonne(personne: Pick<Personne, "userId">): StatutDePersonne {
  return personne.userId == null || personne.userId === "" ? "invite" : "actif";
}

/** Ce que l'annuaire recopie d'une fiche membre. */
export interface EntreeDAnnuaire {
  displayName: string;
  email: string;
  userId: string | null;
  archivedAt: Date | null;
}

/**
 * CE QUE L'ENTREE D'ANNUAIRE DOIT PORTER, deduit de la fiche membre. Pure :
 * le SDK, les Functions et les scripts l'appliquent a l'identique (ces deux
 * derniers en recopient le code, `functions/` ne dependant d'aucun `@taki/*`).
 *
 * `archivedAt` : une fiche archivee ou supprimee garde sa date ; une fiche
 * DESACTIVEE archive l'entree a `now`, pour que le selecteur ne la propose
 * plus tout en laissant son nom sur les fiches du passe.
 */
export function entreeDAnnuaire(
  fiche: Pick<Member, "email"> &
    Partial<Pick<Member, "displayName" | "userId" | "disabled" | "archivedAt" | "deletedAt">>,
  now: Date,
): EntreeDAnnuaire {
  return {
    displayName: fiche.displayName ?? "",
    email: fiche.email,
    userId: fiche.userId ?? null,
    archivedAt: fiche.archivedAt ?? fiche.deletedAt ?? (fiche.disabled === true ? now : null),
  };
}
